import React from 'react';

interface ArrowRightIconProps {
  className?: string;
  /**
   * Visual variant. `filled` (default) uses the Heroicons mini solid style
   * (20x20 viewBox, `fill="currentColor"`). `stroked` uses the Lucide-parity
   * 24x24 outline style used by the visibility checker hero. `hairline` is the
   * marketing design system's 12x12 arrow at a 1.1 stroke – the glyph every
   * forward button and text link on the redesigned pages carries.
   */
  variant?: 'filled' | 'stroked' | 'hairline';
  strokeWidth?: number | string;
}

/**
 * Right-arrow icon. Two visual variants share this name because they were
 * both inlined across the codebase before consolidation. Pick the variant
 * that matches the surrounding chrome – filled for compact "text + arrow"
 * CTAs (DESIGN_SYSTEM.md § Color System), stroked for the larger hero
 * affordances.
 */
export function ArrowRightIcon({
  className = 'h-4 w-4',
  variant = 'filled',
  strokeWidth = 2,
}: ArrowRightIconProps): React.ReactElement {
  if (variant === 'hairline') {
    return (
      <svg
        className={className}
        fill="none"
        viewBox="0 0 12 12"
        stroke="currentColor"
        strokeWidth="1.1"
        aria-hidden="true"
      >
        <path d="M1 6h9M6 2l4 4-4 4" />
      </svg>
    );
  }
  if (variant === 'stroked') {
    return (
      <svg
        className={className}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={strokeWidth}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
        />
      </svg>
    );
  }
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}
